import React, { useEffect, useState } from "react";
import axios from "../axios";

axios.defaults.withCredentials = true;

export function AdminDashboardViewByPerson() {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [selectedUser, setSelectedUser] = useState(null);
    const [administratorsData, setAdministratorsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
        // When entering fullscreen, scroll to top
        if (!isFullScreen) {
            window.scrollTo(0, 0);
        }
    };

    useEffect(() => {
        async function fetchAgentsData(year) {
            setLoading(true);
            try {
                const response = await axios.get(
                    `/api/admin/metrics/administrators/${year}`,
                    {
                        withCredentials: true,
                    }
                );

                setAdministratorsData(response.data.data);

                if (response.data.data.length > 0) {
                    setSelectedUser(response.data.data[0]);
                }
                setError(null);
            } catch (error) {
                console.error("Error fetching agents data:", error);
                setError(
                    "Napaka pri nalaganju podatkov. Prosim, poskusite znova kasneje."
                );
            } finally {
                setLoading(false);
            }
        }

        fetchAgentsData(selectedYear);
    }, [selectedYear]);

    return (
        <div className="relative flex flex-col w-full h-screen p-4 bg-gray-50">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    Statistika administratorjev
                </h1>
                <p className="mt-1 text-gray-600">
                    Tukaj lahko urejate in pregledujete statistiko
                    administratorjev. Vsi podatki se samodejno shranjujejo.
                </p>
            </div>

            <div className="mb-8 text-center">
                <div className="mb-8 text-center">
                    <h1 className="mb-2 text-xl font-bold sm:text-2xl lg:text-3xl">
                        Pogled po osebi
                    </h1>
                </div>
                <div className="flex justify-center gap-2">
                    <select
                        value={selectedYear}
                        onChange={(e) =>
                            setSelectedYear(Number(e.target.value))
                        }
                        className="px-4 py-2 bg-white border rounded-lg"
                    >
                        <option value={2024}>2024</option>
                        <option value={2025}>2025</option>
                        <option value={2026}>2026</option>
                    </select>
                    {selectedUser && (
                        <select
                            value={selectedUser.name}
                            onChange={(e) => {
                                const found = administratorsData.find(
                                    (admin) => admin.name === e.target.value
                                );
                                if (found) {
                                    setSelectedUser(found);
                                } else {
                                    setSelectedUser(administratorsData[0]);
                                }
                            }}
                            className="px-4 py-2 bg-white border rounded-lg"
                        >
                            {administratorsData.map((item) => (
                                <option key={item.name} value={item.name}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center h-64">
                    <div className="w-12 h-12 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                </div>
            )}

            {(error || administratorsData.length === 0) && !loading && (
                <div className="flex flex-col items-center justify-center h-64">
                    <h2 className="mb-2 text-xl font-semibold text-gray-800">
                        Ni dodeljenih podatkov
                    </h2>
                    <p className="text-gray-600">
                        Za vas trenutno ni konfiguriran noben podatek. Prosim,
                        kontaktirajte administratorja.
                    </p>
                </div>
            )}

            {!loading && !error && selectedUser ? (
                isFullScreen ? (
                    <FullScreenContent
                        selectedUser={selectedUser}
                        selectedYear={selectedYear}
                        toggleFullScreen={toggleFullScreen}
                    />
                ) : (
                    <Content
                        selectedUser={selectedUser}
                        selectedYear={selectedYear}
                        toggleFullScreen={toggleFullScreen}
                        isFullScreen={isFullScreen}
                    />
                )
            ) : null}
        </div>
    );
}

function Content({
    selectedUser,
    selectedYear,
    toggleFullScreen,
    isFullScreen,
}) {
    return (
        <div className="relative flex-1 overflow-hidden bg-white rounded-lg shadow-md">
            {/* Header with name and fullscreen button */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-white border-b">
                <h2 className="text-lg font-medium text-gray-800">
                    {selectedUser.name} Statistika - {selectedYear}
                </h2>
                <button
                    className="px-3 py-1 text-white transition-colors bg-blue-600 rounded hover:bg-blue-700"
                    onClick={toggleFullScreen}
                >
                    {isFullScreen
                        ? "Izhod iz celozaslonskega načina"
                        : "Celozaslonski prikaz"}
                </button>
            </div>

            {/* Add padding to account for the header */}
            <div className="h-full pt-16">
                <iframe
                    src={selectedUser.sheetUrl}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{
                        background: "white",
                        border: "none",
                        minHeight: "700px",
                    }}
                    title={selectedUser.name}
                    allowFullScreen
                    loading="lazy"
                />
            </div>
        </div>
    );
}

function FullScreenContent({ selectedUser, selectedYear, toggleFullScreen }) {
    return (
        <div
            className="fixed inset-0 z-50 flex flex-col bg-black bg-opacity-80"
            style={{ overflow: "hidden" }}
        >
            <div className="flex items-center justify-between p-4 bg-white bg-opacity-10">
                <h2 className="font-medium text-white">
                    {selectedUser.name} Statistika - {selectedYear}
                </h2>
                <button
                    className="px-4 py-2 font-medium text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700"
                    onClick={toggleFullScreen}
                >
                    Izhod iz celozaslonskega načina
                </button>
            </div>
            <div className="flex items-center justify-center flex-1">
                <iframe
                    src={selectedUser.sheetUrl}
                    className="w-full h-full"
                    frameBorder="0"
                    style={{ background: "white", border: "none" }}
                    title="Statistika"
                    allowFullScreen
                    loading="lazy"
                />
            </div>
        </div>
    );
}
